import React, { useState, useEffect } from 'react';
import { X, MapPin, Save, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { cityApi } from '../services/api';

const CityModal = ({ isOpen, onClose, onSave, city }) => {
  const [formData, setFormData] = useState({
    cityName: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [checkingName, setCheckingName] = useState(false);
  const [nameAvailable, setNameAvailable] = useState(null);

  const isEditing = !!city;

  useEffect(() => {
    if (city) {
      setFormData({
        cityName: city.cityName || ''
      });
    } else {
      setFormData({
        cityName: ''
      });
    }
    setErrors({});
    setApiError('');
    setNameAvailable(null);
  }, [city]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Check city name availability for new cities
    if (name === 'cityName' && !isEditing && value.trim().length > 2) {
      checkCityNameAvailability(value.trim());
    } else if (name === 'cityName' && isEditing && value.trim().length > 2) {
      checkCityNameAvailability(value.trim(), city.cityID);
    } else {
      setNameAvailable(null);
    }
  };

  const checkCityNameAvailability = async (cityName, excludeId = null) => {
    try {
      setCheckingName(true);
      const response = await cityApi.checkCityName(cityName, excludeId);
      setNameAvailable(response);
    } catch (error) {
      console.error('Error checking city name:', error);
      setNameAvailable(null);
    } finally {
      setCheckingName(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.cityName.trim()) {
      newErrors.cityName = 'City name is required';
    } else if (formData.cityName.trim().length > 50) {
      newErrors.cityName = 'City name cannot exceed 50 characters';
    } else if (formData.cityName.trim().length > 2 && nameAvailable === false) {
      newErrors.cityName = 'City name already exists';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setApiError('');
    
    try {
      const cityData = {
        cityName: formData.cityName.trim()
      };

      await onSave(cityData);
      console.log('City saved successfully');
    } catch (error) {
      console.error('Error saving city:', error);
      
      // Handle specific error messages
      let errorMessage = 'Failed to save city. Please try again.';
      
      if (error.message) {
        if (error.message.includes('already exists')) {
          errorMessage = `City name "${formData.cityName.trim()}" already exists. Please choose a different name.`;
        } else if (error.message.includes('Bad Request')) {
          errorMessage = error.message;
        } else {
          errorMessage = error.message;
        }
      }
      
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />
        
        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-2xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    {isEditing ? 'Edit City' : 'Add New City'}
                  </h3>
                  <p className="text-blue-100">
                    {isEditing ? 'Update city information' : 'Create a new city'}
                  </p>
                  {loading && (
                    <div className="flex items-center justify-center space-x-2 text-blue-200 text-sm mt-1">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Saving city...</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={loading}
                className="text-white hover:text-gray-200 transition-colors disabled:opacity-50"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="px-6 py-6">
            {/* API Error Message */}
            {apiError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                <strong>Error:</strong> {apiError}
                <button 
                  onClick={() => setApiError('')}
                  className="float-right font-bold text-red-700 hover:text-red-900"
                >
                  ×
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                             {/* City Name */}
               <div>
                 <label className="block text-sm font-medium text-gray-700 mb-2">
                   City Name *
                 </label>
                 <div className="relative">
                   <input
                     type="text"
                     name="cityName"
                     value={formData.cityName}
                     onChange={handleInputChange}
                     className={`w-full px-4 py-3 pr-12 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                       errors.cityName ? 'border-red-500' : 
                       nameAvailable === true ? 'border-green-500' :
                       nameAvailable === false ? 'border-red-500' : 'border-gray-300'
                     }`}
                     placeholder="e.g., Colombo, Kandy, Galle"
                     disabled={loading}
                     maxLength={50}
                   />
                   {/* Real-time validation icon */}
                   {formData.cityName.trim().length > 2 && (
                     <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                       {checkingName ? (
                         <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                       ) : nameAvailable === true ? (
                         <CheckCircle className="w-5 h-5 text-green-500" />
                       ) : nameAvailable === false ? (
                         <XCircle className="w-5 h-5 text-red-500" />
                       ) : null}
                     </div>
                   )}
                 </div>
                 {errors.cityName && (
                   <p className="mt-1 text-sm text-red-600">{errors.cityName}</p>
                 )}
                 {formData.cityName.trim().length > 2 && nameAvailable === true && !errors.cityName && (
                   <p className="mt-1 text-sm text-green-600">City name is available</p>
                 )}
                 {formData.cityName.trim().length > 2 && nameAvailable === false && !errors.cityName && (
                   <p className="mt-1 text-sm text-red-600">City name already exists</p>
                 )}
                 <p className="mt-1 text-xs text-gray-500">
                   {formData.cityName.length}/50 characters
                 </p>
               </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-6 py-3 rounded-xl font-semibold text-white transition-all duration-200 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 shadow-lg hover:shadow-xl disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center justify-center space-x-2">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>{isEditing ? 'Updating...' : 'Creating...'}</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Save className="w-5 h-5" />
                      <span>{isEditing ? 'Update City' : 'Create City'}</span>
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Bottom Actions */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={loading}
                className="px-6 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CityModal;
